import { useEffect, memo, useCallback, startTransition } from 'react';

import { QueryStatus } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { RowComponentProps, useListCallbackRef } from 'react-window';

import { StickyScrollFade } from '@/components/atoms/StickyScrollFade/StickyScrollFade';
import { RowInfiniteLoader } from '@/components/organisms/InfiniteLoader/RowInfiniteLoader';
import { rowIdProps } from '@/components/organisms/InfiniteLoader/useRowIdHeightCache';
import { useDeskId } from '@/hooks/useDeskId';
import useDesktopNotifications from '@/hooks/useDesktopNotifications';
import { useScrollFade } from '@/hooks/useScrollFade';
import { cn } from '@/lib/utils';
import { selectIsBannerOpen } from '@/redux/banners/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectSessionId } from '@/redux/session/selectors';
import { selectIframeView } from '@/redux/user/selectors';
import { Conversation, LastMessage } from '@/shared/types/chat';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { switchChannelName } from '@/util/switchChannel';
import { decodeHTMLEntities } from '@/util/util';

import { Nav } from './LeftSidebar/Nav';
import { useRtmConversations } from '../hooks/useRtmConversations';
import MessagePreview, { TILE_HEIGHT } from './MessagePreview/MessagePreview';
import { ConversationsRenderProp, MessageListContainer } from './MessagesList/MessageListContainer';
import MessagePreviewSkeleton from './MessagesList/MessagePreviewSkeleton/MessagePreviewSkeleton';

// Returns a string, never a JSX fragment: a fresh element misses MessagePreview's memo.
const formatLastMessage = (message: LastMessage, t: TFunction, userName?: string): string => {
  if (message?.author_type === 'brain' && message?.body?.type === 'switch_channel') {
    return t('conversation.switch_channel_sent', {
      0: switchChannelName(t, (message.body as { channel?: string }).channel),
    });
  }
  if (message?.author_type === 'brain' && message?.body?.type !== 'text') {
    return `${t('conversation.sent')} ${message?.body?.type ?? ''} `;
  }
  if (
    (message?.author_type === 'visitor' || message?.author_type === 'agent') &&
    message.body?.attachments?.length > 0
  ) {
    return `${userName ?? ''} ${t('conversation.sent').toLowerCase()} ${message?.body?.attachments[0].type}`;
  }

  const formatedText = decodeHTMLEntities(message?.text);
  return formatedText;
};

/**
 * One identity per row, used for react's key, for the height cache and for the stamp on the
 * element the cache measures. The list is sorted by last activity, so a single message re-indexes
 * the whole window and anything keyed by slot index describes the previous order.
 */
export const conversationRowId = (index: number, conversations: Conversation[]) =>
  conversations?.[index]?.session_id ?? `placeholder-${index}`;

interface ConversationRowProps {
  conversations: Conversation[];
  hasNextPage: boolean;
  selectedSessionId: string;
  handleMessageClick: (id: string) => void;
  t: TFunction;
}

export const ConversationRow = ({
  index,
  style,
  conversations,
  hasNextPage,
  selectedSessionId,
  handleMessageClick,
  t,
}: RowComponentProps<ConversationRowProps>) => {
  const conversation = conversations[index];
  if (conversation) {
    const {
      session_id,
      last_message_at,
      last_message,
      agent_unread_count,
      channel,
      context,
      assignee_agent_id,
      assignee_brain_id,
      status,
    } = conversation;
    const visitorName = context?.user?.display_name ?? t('common.anonymous');
    return (
      <div style={style} {...rowIdProps(conversationRowId(index, conversations))}>
        <MessagePreview
          timestamp={last_message_at}
          userName={visitorName}
          message={formatLastMessage(last_message, t, visitorName)}
          unreadCount={agent_unread_count}
          channel={channel}
          onClick={handleMessageClick}
          sessionId={session_id}
          isSelected={selectedSessionId === session_id}
          isFirst={index === 0}
          assigneeAgentId={assignee_agent_id}
          assigneeBrainId={assignee_brain_id}
          status={status}
        />
      </div>
    );
  }
  if (hasNextPage) {
    return (
      <div style={style} {...rowIdProps(conversationRowId(index, conversations))}>
        <MessagePreviewSkeleton />
      </div>
    );
  }
  return null;
};

interface ConversationListProps {
  conversations: Conversation[];
  hasNextPage: boolean;
  listStatus: QueryStatus;
  fetchNextPage: () => void;
  sessionId: string;
  handleMessageClick: (id: string) => void;
  t: TFunction;
}

const ConversationList = memo(
  ({
    conversations,
    hasNextPage,
    listStatus,
    fetchNextPage,
    sessionId,
    handleMessageClick,
    t,
  }: ConversationListProps) => {
    const isLoading = listStatus === 'pending';
    const hasNoConversations = conversations?.length === 0 && !isLoading;
    const itemCount = conversations?.length + (hasNextPage ? 1 : 0);

    // A new function on every reorder, which is what makes react-window re-observe the rows that
    // just entered the window. See useRowIdHeightCache.
    const getRowId = useCallback(
      (index: number) => conversationRowId(index, conversations),
      [conversations]
    );

    // react-window owns the scroll element, so resolve it via the list's
    // imperative handle and feed it to the shared scroll-fade hook. Mirrors the
    // bottom fade on the left Nav panel.
    const [scrollList, setScrollList] = useListCallbackRef();
    const { showFade } = useScrollFade<HTMLDivElement>('bottom', scrollList?.element);

    const canWrite = useSelector((state: RootState) =>
      getPermissions(state, 'conversations', actions.WRITE)
    );

    const isItemLoaded = (index: number) => {
      if (hasNextPage) {
        return index < conversations?.length;
      }
      return true;
    };

    const loadMoreItems = () => {
      if (!isLoading && hasNextPage) {
        fetchNextPage();
      }
    };

    const isBannerOpen = useSelector(selectIsBannerOpen);
    const bannerOffset = isBannerOpen ? ' - 36px' : '';
    const readOnlyOffset = !canWrite ? ' - 36px' : '';

    return (
      <>
        <div
          className="relative overflow-hidden"
          style={{
            height: `calc(100vh - var(--newheader-height) - 72px${bannerOffset}${readOnlyOffset})`,
          }}
        >
          <div
            className={cn(
              conversations?.length > 0 && 'h-full',
              hasNoConversations && 'flex h-full items-center justify-center'
            )}
          >
            {hasNoConversations ? (
              <p className="max-w-full px-4 text-center break-words whitespace-normal">
                {t('conversation.no_conversations')}
              </p>
            ) : (
              <RowInfiniteLoader<ConversationRowProps>
                itemCount={itemCount}
                loadMoreItems={loadMoreItems}
                isItemLoaded={isItemLoaded}
                itemSize={TILE_HEIGHT}
                dynamicRowHeight
                className="flex pb-2"
                listRef={setScrollList}
                rowComponent={ConversationRow}
                rowId={getRowId}
                rowProps={{
                  conversations,
                  hasNextPage,
                  selectedSessionId: sessionId,
                  handleMessageClick,
                  t,
                }}
              />
            )}

            {isLoading &&
              Array(5)
                .fill('')
                // eslint-disable-next-line react/no-array-index-key
                .map((_, i) => <MessagePreviewSkeleton key={i} />)}
          </div>
          <StickyScrollFade show={showFade} direction="bottom" className="bottom-0 h-16" />
        </div>
      </>
    );
  }
);

ConversationList.displayName = 'ConversationList';

export const SubNavConversationNew = memo(() => {
  const { t } = useTranslation();
  const { allowNotifications } = useDesktopNotifications();
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const sessionId = useSelector(selectSessionId);

  const deskId = useDeskId();
  const iframeView = useSelector(selectIframeView);

  useRtmConversations(deskId);

  useEffect(() => {
    allowNotifications();
  }, [allowNotifications]);

  const handleMessageClick = useCallback(
    (id: string) => {
      startTransition(() => {
        navigate(`/${slug}/chats/${deskId}/conversations/${id}`);
      });
    },
    [deskId, navigate, slug]
  );

  // Stable identity, or a resize drag re-runs the conversations query tree on every pointermove.
  const renderList = useCallback<ConversationsRenderProp>(
    ({ conversations, hasNextPage, listStatus, fetchNextPage }) => (
      <ConversationList
        conversations={conversations}
        hasNextPage={hasNextPage}
        listStatus={listStatus}
        fetchNextPage={fetchNextPage}
        sessionId={sessionId}
        handleMessageClick={handleMessageClick}
        t={t}
      />
    ),
    [sessionId, handleMessageClick, t]
  );

  return (
    <>
      {!iframeView && <Nav />}
      <MessageListContainer>{renderList}</MessageListContainer>
    </>
  );
});

SubNavConversationNew.displayName = 'SubNavConversationNew';
